// Маршруты REST API — общие для сервера (регистрация) и клиента (вызовы).
// Сервер передаёт плейсхолдеры (':blogId'), клиент — реальные идентификаторы. Администрирование
// платформы — в AdminApiRoutes (admin-api-routes.ts): этот файл попадает в скрипт gr-sync скила.
type Id = string;

const blog = (blogId: Id): string => `/api/blogs/${blogId}`;

export const ApiRoutes = {
  // OPS-UC-1, DEMO-UC-1: проверка состояния и сведения о режиме работы сервера.
  operations: {
    health: '/api/health',
    info: '/api/operations/info',
  },
  // CFG-UC-1: публичная конфигурация интерфейса.
  config: {
    public: '/api/config',
  },
  // PREF-UC-3: личные настройки вошедшего пользователя.
  preferences: {
    settings: '/api/me/settings',
    setting: (key: string): string => `/api/me/settings/${key}`,
  },
  // SYS-UC-13: соединение событий (WebSocket, ADR-012).
  system: {
    realtime: '/api/realtime',
  },
  // TASK-UC-3…5: фоновые задачи блога (ADR-019).
  tasks: {
    list: (blogId: Id): string => `${blog(blogId)}/tasks`,
    byId: (blogId: Id, taskId: Id): string => `${blog(blogId)}/tasks/${taskId}`,
    cancel: (blogId: Id, taskId: Id): string => `${blog(blogId)}/tasks/${taskId}/cancel`,
    resume: (blogId: Id, taskId: Id): string => `${blog(blogId)}/tasks/${taskId}/resume`,
  },
  auth: {
    login: '/api/auth/login',
    // IAM-UC-14
    register: '/api/auth/register',
    logout: '/api/auth/logout',
    me: '/api/auth/me',
    password: '/api/auth/me/password',
    preferences: '/api/auth/me/preferences',
  },
  // IAM-UC-9, IAM-UC-10: свои ключи API.
  apiKeys: {
    mine: '/api/auth/me/api-keys',
    mineById: (keyId: Id): string => `/api/auth/me/api-keys/${keyId}`,
    revokeMine: (keyId: Id): string => `/api/auth/me/api-keys/${keyId}/revoke`,
  },
  // API для интеграций (ADR-015): проверка ключа (IAM-UC-12).
  integration: {
    me: '/api/integration/me',
  },
  // KIT-UC-1, KIT-UC-2: справочник API, скил и скрипт синхронизации (адреса — публичный контракт).
  toolkit: {
    guide: '/api/toolkit/guide.md',
    skill: '/api/toolkit/skill.md',
    skillZip: '/api/toolkit/skill.zip',
    syncScript: '/api/toolkit/gr-sync.mjs',
  },
  // PLAN-UC-5: свой тариф.
  plans: {
    mine: '/api/me/plan',
  },
  blogs: {
    list: '/api/blogs',
    byId: (blogId: Id): string => blog(blogId),
    members: (blogId: Id): string => `${blog(blogId)}/members`,
    member: (blogId: Id, userId: Id): string => `${blog(blogId)}/members/${userId}`,
  },
  entities: {
    types: (blogId: Id): string => `${blog(blogId)}/entity-types`,
    type: (blogId: Id, typeId: Id): string => `${blog(blogId)}/entity-types/${typeId}`,
    postFields: (blogId: Id): string => `${blog(blogId)}/post-fields`,
    listByType: (blogId: Id, typeId: Id): string => `${blog(blogId)}/entity-types/${typeId}/entities`,
    byId: (blogId: Id, entityId: Id): string => `${blog(blogId)}/entities/${entityId}`,
  },
  posts: {
    statuses: (blogId: Id): string => `${blog(blogId)}/post-statuses`,
    statusesOrder: (blogId: Id): string => `${blog(blogId)}/post-statuses/order`,
    status: (blogId: Id, statusId: Id): string => `${blog(blogId)}/post-statuses/${statusId}`,
    formats: (blogId: Id): string => `${blog(blogId)}/post-formats`,
    format: (blogId: Id, formatId: Id): string => `${blog(blogId)}/post-formats/${formatId}`,
    list: (blogId: Id): string => `${blog(blogId)}/posts`,
    byId: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}`,
    media: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}/media`,
    setStatus: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}/status`,
    variant: (blogId: Id, postId: Id, channelId: Id): string => `${blog(blogId)}/posts/${postId}/variants/${channelId}`,
    readiness: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}/readiness`,
    schedule: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}/schedule`,
    publications: (blogId: Id, postId: Id): string => `${blog(blogId)}/posts/${postId}/publications`,
  },
  media: {
    list: (blogId: Id): string => `${blog(blogId)}/media`,
    byId: (blogId: Id, mediaId: Id): string => `${blog(blogId)}/media/${mediaId}`,
    original: (blogId: Id, mediaId: Id): string => `${blog(blogId)}/media/${mediaId}/original`,
    thumbnail: (blogId: Id, mediaId: Id): string => `${blog(blogId)}/media/${mediaId}/thumbnail`,
    rendition: (blogId: Id, mediaId: Id): string => `${blog(blogId)}/media/${mediaId}/rendition`,
    traffic: (blogId: Id): string => `${blog(blogId)}/media-traffic`,
    // MED-UC-8, MED-UC-9
    storage: (blogId: Id): string => `${blog(blogId)}/media-storage`,
    recompress: (blogId: Id): string => `${blog(blogId)}/media-storage/recompress`,
  },
  channels: {
    list: (blogId: Id): string => `${blog(blogId)}/channels`,
    byId: (blogId: Id, channelId: Id): string => `${blog(blogId)}/channels/${channelId}`,
    slots: (blogId: Id, channelId: Id): string => `${blog(blogId)}/channels/${channelId}/slots`,
  },
  scheduling: {
    schemes: (blogId: Id): string => `${blog(blogId)}/schemes`,
    scheme: (blogId: Id, schemeId: Id): string => `${blog(blogId)}/schemes/${schemeId}`,
    queue: (blogId: Id): string => `${blog(blogId)}/queue`,
    queueOrder: (blogId: Id): string => `${blog(blogId)}/queue/order`,
    queueItem: (blogId: Id, itemId: Id): string => `${blog(blogId)}/queue/${itemId}`,
    calendar: (blogId: Id): string => `${blog(blogId)}/calendar`,
  },
  publishing: {
    due: (blogId: Id): string => `${blog(blogId)}/publications/due`,
    byId: (blogId: Id, publicationId: Id): string => `${blog(blogId)}/publications/${publicationId}`,
    package: (blogId: Id, publicationId: Id): string => `${blog(blogId)}/publications/${publicationId}/package`,
    packageZip: (blogId: Id, publicationId: Id): string => `${blog(blogId)}/publications/${publicationId}/package.zip`,
    packageFile: (blogId: Id, publicationId: Id, index: string): string =>
      `${blog(blogId)}/publications/${publicationId}/package/files/${index}`,
    published: (blogId: Id, publicationId: Id): string => `${blog(blogId)}/publications/${publicationId}/published`,
    cancel: (blogId: Id, publicationId: Id): string => `${blog(blogId)}/publications/${publicationId}/cancel`,
  },
} as const;

/** Параметры маршрутов в виде плейсхолдеров Fastify. */
export const P = {
  blogId: ':blogId',
  userId: ':userId',
  keyId: ':keyId',
  planId: ':planId',
  typeId: ':typeId',
  entityId: ':entityId',
  statusId: ':statusId',
  formatId: ':formatId',
  postId: ':postId',
  mediaId: ':mediaId',
  channelId: ':channelId',
  schemeId: ':schemeId',
  itemId: ':itemId',
  publicationId: ':publicationId',
  taskId: ':taskId',
  index: ':index',
} as const;
